/**
 * PreQuestion 설정 데이터 타입 정의
 */

/**
 * 기본 Input 설정 스키마
 * @typedef {Object} BaseInputConfig
 * @property {string} inputType - Input 타입 ('Text', 'MultiSelect', 'DoubleText')
 * @property {string} question - 질문 텍스트
 * @property {string} [label] - 라벨 텍스트
 * @property {string} [placeholder] - 플레이스홀더 텍스트
 * @property {Object} [validation] - 검증 규칙
 * @property {string} [warning] - 경고 메시지
 * @property {Array} [options] - 선택 옵션들 (MultiSelect용)
 */

/**
 * TextInput 설정
 * @typedef {BaseInputConfig} TextInputConfig
 * @property {'Text'} inputType
 * @property {Object} validation
 * @property {number} validation.min - 최소값
 * @property {number} validation.max - 최대값
 */

/**
 * MultiSelectInput 설정
 * @typedef {BaseInputConfig} MultiSelectInputConfig
 * @property {'MultiSelect'} inputType
 * @property {Array<string>} options - 선택 가능한 옵션들
 */

/**
 * DoubleTextInput 설정
 * @typedef {BaseInputConfig} DoubleTextInputConfig
 * @property {'DoubleText'} inputType
 * @property {Object} firstField - 첫 번째 입력 필드 설정
 * @property {string} firstField.label
 * @property {string} firstField.placeholder
 * @property {Object} firstField.validation
 * @property {Object} secondField - 두 번째 입력 필드 설정
 * @property {string} secondField.label
 * @property {string} secondField.placeholder
 * @property {Object} secondField.validation
 */

/**
 * PreQuestion 시나리오 설정
 * @typedef {Object} PreQuestionScenario
 * @property {string} id - 시나리오 ID
 * @property {string} name - 시나리오 이름
 * @property {Array<BaseInputConfig>} questions - 질문 목록
 * @property {string} [completionMessage] - 완료 메시지
 * @property {Object} [metadata] - 추가 메타데이터
 */

/**
 * PreQuestion 설정 검증 함수
 * @param {Object} config - 검증할 설정 객체
 * @returns {Object} 검증 결과
 */
export const validatePreQuestionConfig = (config) => {
  const errors = [];
  const warnings = [];

  // 필수 필드 검증
  if (!config.id) {
    errors.push('시나리오 ID가 필요합니다');
  }

  if (!config.questions || !Array.isArray(config.questions)) {
    errors.push('questions 배열이 필요합니다');
  } else {
    config.questions.forEach((question, index) => {
      if (!question.type) {
        errors.push(`질문 ${index + 1}: inputType이 필요합니다`);
      }

      // if (!question.question && question.type !== 'group') {
      //   errors.push(`질문 ${index + 1}: question이 필요합니다`);
      // }

      // Input 타입별 검증
      switch (question.type) {
        case 'single':
          if (!question.validation) {
            warnings.push(`질문 ${index + 1}: TextInput에 validation 설정을 권장합니다`);
          }
          break;
        case 'multiSelect':
          if (!question.options || question.options.length === 0) {
            errors.push(`질문 ${index + 1}: MultiSelect에 options가 필요합니다`);
          }
          break;
        case 'group':
          // if (!question.questions || question.questions.length === 0) {
          //   errors.push(`질문 ${index + 1}: Group에 questions가 필요합니다`);
          // }
          break;
        default:
          errors.push(`질문 ${index + 1}: 지원하지 않는 inputType입니다: ${question.inputType}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * 설정 데이터를 정규화하는 함수
 * @param {Object} rawConfig - 원본 설정 데이터
 * @returns {Object} 정규화된 설정 객체
 */
export const normalizePreQuestionConfig = (rawConfig) => {
  return {
    id: rawConfig.id || 'default',
    name: rawConfig.name || 'Default Scenario',
    questions: rawConfig.questions?.map((question, index) => ({
      ...question,
      order: index,
      validation: question.validation || {},
      options: question.options || [],
    })) || [],
    answersLength: rawConfig.questions.map((question) => question.type === 'group' ? 2 : 1).reduce((acc, curr) => acc + curr, 0) || 0,
    requestMessage: rawConfig.questions[rawConfig.questions.length - 1]?.requestMessage || '알맞게 추천해주세요',
    metadata: rawConfig.metadata || {},
  };
};
